import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useCorporateDetailsController } from "./CorporateDetailsController";
import { GoldenSourceHelper } from "../../app/Switch_Core_React_Standards";
import { CorporateHierarchyRules } from "./CorporateHierarchyRules";
import { fetchOwnershipLinks, assignOwnershipLink, revokeOwnershipLink } from "./ownershipInteractionService";
import { useGraphController } from "../common/visuals/GraphController";
import ForceFieldSpheres from "../common/visuals/ForceFieldSpheres";

/**
 * 🎯 Hierarchy editor backed by Corp.Z_01_06_CorporateEntity_Ownership
 * (many-to-many — a child can have more than one parent/co-owner). This
 * NEVER calls updateHierarchy/registerCorporateEntity — that path targeted
 * usp_Z_01_CorporateEntity_Create, which has no @CorporateEntityId and so
 * silently duplicated the entity on every "save structure" instead of
 * updating it. All linking here goes through assignOwnershipLink/
 * revokeOwnershipLink instead.
 */
export default function ManageStructure({ initialSelectedIds = [], onClose }) {
  const { getRegistry, loading } = useCorporateDetailsController();
  const { nodes, edges: graphEdges, setGraphData } = useGraphController();

  const siteKey = process.env.REACT_APP_SITE_KEY;

  const [registry, setRegistry] = useState([]);
  const [ownershipEdges, setOwnershipEdges] = useState([]);
  const [draftLinks, setDraftLinks] = useState([]); // [{parentEntityId, childEntityId}]
  const [searchTerm, setSearchTerm] = useState("");
  const [rootId, setRootId] = useState(null);
  const [branchIds, setBranchIds] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const load = async () => {
      const data = await getRegistry();
      const validData = Array.isArray(data) ? data : [];
      setRegistry(validData);

      const links = await fetchOwnershipLinks({ siteKey });
      const normalizedLinks = (links || []).map((l) => ({
        parentEntityId: GoldenSourceHelper.get(l, "ParentEntityId"),
        childEntityId: GoldenSourceHelper.get(l, "ChildEntityId"),
      }));
      setOwnershipEdges(normalizedLinks);
      setDraftLinks(normalizedLinks);

      if (initialSelectedIds.length > 0) {
        const discovered = new Set();

        const findRelatives = (id) => {
          normalizedLinks.forEach((l) => {
            if (l.parentEntityId === id && !discovered.has(l.childEntityId)) {
              discovered.add(l.childEntityId);
              findRelatives(l.childEntityId);
            }
            if (l.childEntityId === id && !discovered.has(l.parentEntityId)) {
              discovered.add(l.parentEntityId);
              findRelatives(l.parentEntityId);
            }
          });
        };

        initialSelectedIds.forEach((id) => {
          discovered.add(id);
          findRelatives(id);
        });

        const branchArray = Array.from(discovered);
        setBranchIds(branchArray);

        const rootNode = branchArray.find((id) =>
          CorporateHierarchyRules.canBeRoot(id, normalizedLinks, [])
        );
        if (rootNode) setRootId(rootNode);
      }
    };
    load();
  }, [getRegistry, initialSelectedIds, siteKey]);

  const filteredList = useMemo(() => {
    if (!Array.isArray(registry)) return [];
    const base = branchIds.length > 0
      ? registry.filter((c) => branchIds.includes(GoldenSourceHelper.get(c, "CorporateEntityId")))
      : registry;

    return base.filter((c) =>
      (GoldenSourceHelper.get(c, "EntityName") || "").toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [registry, branchIds, searchTerm]);

  /** 🪐 GRAPH MAPPING (supports multiple incoming edges per child) */
  useEffect(() => {
    if (registry.length === 0) return;

    const newNodes = filteredList.map((item) => ({
      id: String(GoldenSourceHelper.get(item, "CorporateEntityId")),
      label: GoldenSourceHelper.get(item, "EntityName"),
      type: "COMPANY",
    }));

    const visibleIds = new Set(filteredList.map((item) => GoldenSourceHelper.get(item, "CorporateEntityId")));
    const newEdges = draftLinks
      .filter((l) => visibleIds.has(l.parentEntityId) && visibleIds.has(l.childEntityId))
      .map((l) => ({ source: String(l.parentEntityId), target: String(l.childEntityId) }));

    setGraphData(newNodes, newEdges);
  }, [registry, filteredList, draftLinks, setGraphData]);

  const isLinked = (parentId, childId) =>
    draftLinks.some((l) => l.parentEntityId === parentId && l.childEntityId === childId);

  const toggleLink = (parentId, childId) => {
    setDraftLinks((prev) => {
      if (prev.some((l) => l.parentEntityId === parentId && l.childEntityId === childId)) {
        return prev.filter((l) => !(l.parentEntityId === parentId && l.childEntityId === childId));
      }
      return [...prev, { parentEntityId: parentId, childEntityId: childId }];
    });
  };

  const linkKey = (l) => `${l.parentEntityId}|${l.childEntityId}`;

  const handleSave = async () => {
    setError("");
    const originalKeys = new Set(ownershipEdges.map(linkKey));
    const draftKeys = new Set(draftLinks.map(linkKey));

    const toAdd = draftLinks.filter((l) => !originalKeys.has(linkKey(l)));
    const toRemove = ownershipEdges.filter((l) => !draftKeys.has(linkKey(l)));

    if (toAdd.length === 0 && toRemove.length === 0) return onClose();

    setSaving(true);
    try {
      await Promise.all([
        ...toAdd.map((l) => assignOwnershipLink(l.parentEntityId, l.childEntityId, null, { siteKey })),
        ...toRemove.map((l) => revokeOwnershipLink(l.parentEntityId, l.childEntityId, { siteKey })),
      ]);
      onClose();
    } catch (err) {
      setError(err.message || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="card shadow-sm border-0 p-4 animate__animated animate__fadeIn">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h5 className="fw-bold mb-0">Branch Hierarchy Editor</h5>
          <span className="extra-small text-muted text-uppercase fw-bold">Lineage Mode Active — co-ownership supported</span>
        </div>
        <div className="d-flex gap-2">
          <button className="btn btn-sm btn-outline-secondary extra-small fw-bold" onClick={onClose}>CANCEL</button>
          <button className="btn btn-sm btn-success extra-small fw-bold" onClick={handleSave} disabled={loading || saving}>
            {saving ? "COMMITTING..." : "SAVE STRUCTURE"}
          </button>
        </div>
      </div>

      {error && <div className="alert alert-danger py-2 small">{error}</div>}

      <input
        type="text" className="form-control form-control-sm mb-4"
        placeholder="Filter within this branch..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
      />

      <div className="row g-4">
        <div className="col-md-5 border-end">
          <h6 className="extra-small fw-bold text-muted mb-3">BRANCH ENTITIES</h6>
          <div className="list-group list-group-flush overflow-auto" style={{ maxHeight: "400px" }}>
            {filteredList.map((c) => {
              const id = GoldenSourceHelper.get(c, "CorporateEntityId");
              const isRootEligible = CorporateHierarchyRules.canBeRoot(id, ownershipEdges, draftLinks);
              return (
                <div key={id} className={`list-group-item d-flex justify-content-between align-items-center px-2 ${rootId === id ? "bg-light" : ""}`}>
                  <span className={`small ${!isRootEligible && rootId !== id ? "text-muted" : "fw-bold"}`}>
                    {GoldenSourceHelper.get(c, "EntityName")}
                  </span>
                  <button
                    className={`btn btn-xs ${rootId === id ? "btn-success" : "btn-outline-dark"} extra-small`}
                    onClick={() => setRootId(id)}
                  >
                    {rootId === id ? "ROOT" : "SET ROOT"}
                  </button>
                </div>
              );
            })}
          </div>
        </div>

        <div className="col-md-7 ps-4">
          <h6 className="extra-small fw-bold text-muted mb-3">PARENTS / CO-OWNERS PER ENTITY</h6>
          {rootId ? (
            <div className="tree-builder">
              <div className="p-2 bg-dark text-white rounded mb-3 small fw-bold">
                <i className="bi bi-building-fill me-2"></i>
                {GoldenSourceHelper.get(registry.find((r) => GoldenSourceHelper.get(r, "CorporateEntityId") === rootId), "EntityName")}
                <span className="ms-2 badge bg-secondary">ROOT</span>
              </div>
              <div className="ms-3 border-start ps-3 overflow-auto" style={{ maxHeight: "320px" }}>
                {filteredList.filter((c) => GoldenSourceHelper.get(c, "CorporateEntityId") !== rootId).map((child) => {
                  const childId = GoldenSourceHelper.get(child, "CorporateEntityId");
                  return (
                    <div key={childId} className="mb-3 p-2 bg-white border-bottom">
                      <div className="small fw-bold mb-1">{GoldenSourceHelper.get(child, "EntityName")}</div>
                      <div className="d-flex flex-wrap gap-2">
                        {filteredList.filter((p) => GoldenSourceHelper.get(p, "CorporateEntityId") !== childId).map((p) => {
                          const parentId = GoldenSourceHelper.get(p, "CorporateEntityId");
                          const checked = isLinked(parentId, childId);
                          const cycleIfChecked = !checked && CorporateHierarchyRules.wouldCreateCycle(childId, parentId, ownershipEdges, draftLinks.filter((l) => l.childEntityId !== childId));
                          return (
                            <label
                              key={parentId}
                              className={`extra-small border rounded px-2 py-1 ${checked ? "bg-dark text-white" : cycleIfChecked ? "bg-light text-muted" : "bg-white"}`}
                              style={{ cursor: cycleIfChecked ? "not-allowed" : "pointer" }}
                            >
                              <input
                                type="checkbox"
                                className="form-check-input me-1"
                                checked={checked}
                                disabled={cycleIfChecked}
                                onChange={() => toggleLink(parentId, childId)}
                              />
                              {GoldenSourceHelper.get(p, "EntityName")}
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : (
            <div className="text-center p-5 border rounded bg-light text-muted small italic">
              Select a ROOT node on the left to begin branch editing.
            </div>
          )}
        </div>
      </div>

      <hr className="my-5" />
      <div className="graph-container">
        <h6 className="extra-small fw-bold text-muted mb-3 text-uppercase">
          <i className="bi bi-globe me-2"></i>Force Field Sphere Visualization
        </h6>
        <ForceFieldSpheres nodes={nodes} edges={graphEdges} height={400} />
      </div>
    </div>
  );
}
